/**
 * Kích hoạt (trigger) — theo mô hình Lark Base Automation:
 *  1. `internal`  Khi dữ liệu TRONG HỆ THỐNG thay đổi: bản ghi mới / trường đổi /
 *                 gắn-gỡ nhãn / điều kiện đạt — trên Khách hàng, Hội thoại, Đơn hàng, Việc, Lịch hẹn.
 *  2. `external`  Khi NHẬN dữ liệu từ ngoài (KiotViet, Sheet, webhook, FMRP) — qua nguồn dữ liệu.
 *  3. `schedule`  Theo lịch (hằng ngày/tuần, cron), có thể kèm điều kiện lọc bản ghi.
 *  4. `manual`    Thủ công: nút bấm trên hồ sơ khách / form gửi.
 * Với 1, 3, 4 dữ liệu đã ở dạng CRM (customer.*, conversation.*, order.*) → không cần khối Ánh xạ.
 */
import type { SampleEvent } from './types'
import type { SchemaField } from './kiotviet'

export type TriggerMode = 'internal' | 'external' | 'schedule' | 'manual'
export const TRIGGER_MODE_LABELS: Record<TriggerMode, { ten: string; moTa: string }> = {
  internal: { ten: 'Dữ liệu trong hệ thống thay đổi', moTa: 'Khách hàng, hội thoại, đơn hàng, việc, lịch hẹn được tạo / sửa / gắn nhãn / đạt điều kiện' },
  external: { ten: 'Nhận dữ liệu từ bên ngoài', moTa: 'KiotViet, Google Sheet, webhook, app bên thứ 3 gửi sự kiện tới' },
  schedule: { ten: 'Theo lịch', moTa: 'Chạy định kỳ (hằng ngày, hằng tuần, cron), có thể quét bản ghi thoả điều kiện' },
  manual: { ten: 'Thủ công', moTa: 'Nhân viên bấm nút trên hồ sơ khách / hội thoại, hoặc khách gửi form' },
}

export type InternalObject = 'customer' | 'conversation' | 'order' | 'task' | 'appointment'
export const INTERNAL_OBJECTS: Record<InternalObject, { ten: string; goc: string; truong: SchemaField[] }> = {
  customer: {
    ten: 'Khách hàng', goc: 'customer',
    truong: [
      { path: 'customer.name', label: 'Tên', type: 'string' },
      { path: 'customer.phone', label: 'SĐT', type: 'string' },
      { path: 'customer.lifecycleStage', label: 'Giai đoạn (lead / customer / vip…)', type: 'string' },
      { path: 'customer.leadScore', label: 'Điểm tiềm năng', type: 'number' },
      { path: 'customer.tags', label: 'Nhãn', type: 'array' },
      { path: 'customer.sentiment', label: 'Cảm xúc AI', type: 'string' },
      { path: 'customer.birthday', label: 'Ngày sinh', type: 'date' },
      { path: 'customer.totalSpent', label: 'Tổng chi tiêu', type: 'number' },
      { path: 'customer.memberRank', label: 'Hạng hội viên', type: 'string' },
    ],
  },
  conversation: {
    ten: 'Hội thoại', goc: 'conversation',
    truong: [
      { path: 'conversation.channel', label: 'Kênh', type: 'string' },
      { path: 'conversation.status', label: 'Trạng thái', type: 'string' },
      { path: 'conversation.assignee', label: 'Người phụ trách', type: 'string' },
      { path: 'conversation.aiMode', label: 'Chế độ AI', type: 'string' },
      { path: 'conversation.tags', label: 'Nhãn', type: 'array' },
      { path: 'conversation.isReplied', label: 'Đã trả lời', type: 'string' },
      { path: 'conversation.lastMessage', label: 'Tin cuối', type: 'string' },
      { path: 'conversation.unreplied_hours', label: 'Giờ chưa trả lời', type: 'number' },
    ],
  },
  order: {
    ten: 'Đơn hàng', goc: 'order',
    truong: [
      { path: 'order.code', label: 'Mã đơn', type: 'string' },
      { path: 'order.status', label: 'Trạng thái', type: 'string' },
      { path: 'order.total', label: 'Tổng tiền', type: 'number' },
      { path: 'order.source', label: 'Nguồn (chat / AI / sàn)', type: 'string' },
      { path: 'order.items[]', label: 'Sản phẩm', type: 'array', children: [
        { path: 'order.items[].name', label: 'Tên SP', type: 'string' },
        { path: 'order.items[].category', label: 'Nhóm hàng', type: 'string' },
        { path: 'order.items[].qty', label: 'Số lượng', type: 'number' },
      ] },
    ],
  },
  task: {
    ten: 'Việc', goc: 'task',
    truong: [
      { path: 'task.title', label: 'Tiêu đề', type: 'string' },
      { path: 'task.status', label: 'Trạng thái', type: 'string' },
      { path: 'task.assignee', label: 'Người nhận', type: 'string' },
      { path: 'task.dueAt', label: 'Hạn', type: 'date' },
    ],
  },
  appointment: {
    ten: 'Lịch hẹn', goc: 'appointment',
    truong: [
      { path: 'appointment.type', label: 'Loại', type: 'string' },
      { path: 'appointment.status', label: 'Trạng thái', type: 'string' },
      { path: 'appointment.at', label: 'Thời điểm', type: 'date' },
      { path: 'appointment.hours_until', label: 'Còn bao nhiêu giờ', type: 'number' },
    ],
  },
}

export type InternalEvent = 'created' | 'field_changed' | 'tag_added' | 'tag_removed' | 'condition_met' | 'deleted'
export const INTERNAL_EVENT_LABELS: Record<InternalEvent, { ten: string; moTa: string }> = {
  created: { ten: 'Bản ghi mới được tạo', moTa: 'Khách mới, hội thoại mới, đơn mới…' },
  field_changed: { ten: 'Một trường thay đổi', moTa: 'Chọn trường; có thể giới hạn "đổi thành giá trị"' },
  tag_added: { ten: 'Được gắn nhãn', moTa: 'Nhân viên hoặc AI gắn nhãn' },
  tag_removed: { ten: 'Bị gỡ nhãn', moTa: '' },
  condition_met: { ten: 'Đạt điều kiện', moTa: 'Mỗi khi bản ghi được lưu và thoả luật (vd điểm ≥ 80)' },
  deleted: { ten: 'Bị xoá / lưu trữ', moTa: '' },
}

/** Sự kiện mẫu nội bộ — payload đã ở dạng CRM, không cần Ánh xạ. */
export const INTERNAL_SAMPLES: SampleEvent[] = [
  {
    id: 'in_customer_rank', source: 'chatmql', event: 'customer.field_changed:customer.memberRank',
    label: 'Khách Nguyễn Văn Hoàng lên hạng Vàng (trường memberRank đổi)',
    payload: { customer: { code: 'KH012004', name: 'Nguyễn Văn Hoàng', phone: '0912345678', lifecycleStage: 'customer', leadScore: 82, tags: ['Hội viên', 'Đang dùng kháng sinh'], memberRank: 'Vàng', memberRank_truoc: 'Bạc', totalSpent: 5320000 } },
  },
  {
    id: 'in_conv_tag_khieu_nai', source: 'chatmql', event: 'conversation.tag_added',
    label: 'Hội thoại Zalo được gắn nhãn "Khiếu nại"',
    payload: { conversation: { id: 'cv_01', channel: 'Zalo cá nhân', status: 'open', assignee: 'Thanh Hương', aiMode: 'auto', tags: ['Khiếu nại'], tag_moi: 'Khiếu nại', lastMessage: 'Hàng về bị móp hộp, vỡ mất một ấm' }, customer: { name: 'Đỗ Công Vịnh', phone: '0976286809', memberRank: 'Hội viên' } },
  },
  {
    id: 'in_order_completed', source: 'chatmql', event: 'order.field_changed:order.status',
    label: 'Đơn ChatMQL DH2026-0917 chuyển sang Hoàn thành',
    payload: { order: { code: 'DH2026-0917', status: 'Hoàn thành', status_truoc: 'Đang giao', total: 470000, source: 'AI', items: [{ name: 'Vạn Phúc Trà - Hộp thiếc 100gr', category: 'Trà xanh', qty: 2 }] }, customer: { name: 'Nguyễn Văn A', phone: '0912345678', memberRank: 'Bạc' } },
  },
  {
    id: 'in_conv_unreplied', source: 'chatmql', event: 'conversation.condition_met',
    label: 'Hội thoại chưa trả lời quá 24 giờ (quét theo lịch)',
    payload: { conversation: { id: 'cv_02', channel: 'Facebook', status: 'open', assignee: '', aiMode: 'manual', tags: [], isReplied: 'false', unreplied_hours: 27, lastMessage: 'Cho mình hỏi giá set biếu' }, customer: { name: 'Lê Thu Hà', phone: '' } },
  },
  {
    id: 'in_manual_button', source: 'chatmql', event: 'manual.button',
    label: 'Nhân viên bấm nút "Gửi ưu đãi sinh nhật" trên hồ sơ khách',
    payload: { customer: { code: 'KH012088', name: 'Trần Thị Lan', phone: '0987654321', birthday: '1990-02-14', memberRank: 'Hội viên' }, nguoiBam: 'Kim Ngân' },
  },
]

/** Mẫu lịch: chạy 9h sáng, quét khách sinh nhật hôm nay. */
export const SCHEDULE_SAMPLES: SampleEvent[] = [
  {
    id: 'sch_birthday', source: 'schedule', event: 'cron',
    label: 'Lịch 09:00 hằng ngày — khách có sinh nhật hôm nay',
    payload: { schedule: { cron: '0 9 * * *', ranAt: '2026-09-22T09:00:00' }, customer: { code: 'KH010211', name: 'Phạm Minh Đức', phone: '0369888777', birthday: '1995-09-22', memberRank: 'Bạc' } },
  },
]
